import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["text", "button"]

    static values = {
        endpoint: String,
        recordType: String,
        recordId: String,
        translateLabel: String,
        originalLabel: String,
        loadingLabel: String,
        errorLabel: String,
        authorLanguage: String,
        contentLanguage: String
    }

    connect() {
        this.isTranslated = false
        this.applyVisibility()

        this.textTargets.forEach((target) => {
            target.dataset.originalText =
                target.dataset.translationOriginalText ||
                target.innerText.trim()
            // Store formatted HTML so "Show original" restores it (not plain text)
            target._translationOriginalHtml = target.innerHTML
        })
    }

    // Re-evaluate visibility whenever a relevant value changes (e.g. after a
    // Turbo Stream replace that updates content_language).
    authorLanguageValueChanged() {
        this.applyVisibility()
    }

    contentLanguageValueChanged() {
        this.applyVisibility()
    }

    applyVisibility() {
        if (!this.hasButtonTarget) return

        const userLanguage = this.normalizeLocale(
            document.body.dataset.language
        )
        const sourceLanguage =
            this.normalizeLocale(this.contentLanguageValue) ||
            this.normalizeLocale(this.authorLanguageValue)

        // Hide while we don't have enough information to decide, or when
        // languages match. Re-show when they differ. Toggling (not just
        // remove) keeps the state consistent if values change at runtime.
        const shouldHide =
            !userLanguage ||
            !sourceLanguage ||
            userLanguage === sourceLanguage

        this.buttonTarget.classList.toggle("hidden", shouldHide)
    }

    normalizeLocale(value) {
        if (!value) return ""
        return value.toString().trim().split("-")[0].toLowerCase()
    }

    async toggle(event) {
        event.preventDefault()

        if (this.isTranslated) {
            this.showOriginal()
            return
        }

        if (this.hasCachedTranslations()) {
            this.showTranslated()
            return
        }

        await this.translate()
    }

    showOriginal() {
        this.textTargets.forEach((target) => {
            target.innerHTML =
                target._translationOriginalHtml ||
                target.dataset.originalText ||
                ""
        })

        this.isTranslated = false
        this.buttonTarget.innerText = this.translateLabelValue
    }

    showTranslated() {
        this.textTargets.forEach((target) => {
            target.innerText =
                target.dataset.translatedText ||
                target.dataset.originalText ||
                ""
        })

        this.isTranslated = true
        this.buttonTarget.innerText = this.originalLabelValue
    }

    hasCachedTranslations() {
        return this.textTargets.every(
            (target) => target.dataset.translatedText
        )
    }

    async translate() {
        this.setButtonState(true, this.loadingLabelValue)

        try {
            const response = await fetch(this.endpointValue, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": this.csrfToken
                },
                body: JSON.stringify({
                    record_type: this.recordTypeValue,
                    record_id: this.recordIdValue,
                    fields: this.fieldsPayload
                })
            })

            const data = await response.json()
            if (!response.ok)
                throw new Error(data.error || "Translation failed")

            this.textTargets.forEach((target) => {
                const field = target.dataset.translationField
                const translatedText =
                    data.translated_fields?.[field]

                if (translatedText) {
                    target.dataset.translatedText = translatedText
                }
            })

            this.showTranslated()
        } catch (error) {
            console.error("Translation failed", error)
            if (this.hasButtonTarget) {
                this.buttonTarget.innerText = this.errorLabelValue
                setTimeout(() => {
                    if (this.hasButtonTarget) {
                        this.buttonTarget.innerText =
                            this.translateLabelValue
                    }
                }, 1500)
            }
        } finally {
            this.setButtonState(false)
        }
    }

    setButtonState(disabled, label = null) {
        if (!this.hasButtonTarget) return

        this.buttonTarget.disabled = disabled
        if (label) this.buttonTarget.innerText = label
    }

    get fieldsPayload() {
        return this.textTargets.reduce((payload, target) => {
            const field = target.dataset.translationField
            const text =
                target.dataset.originalText ||
                target.innerText.trim()

            if (field && text) {
                payload[field] = text
            }

            return payload
        }, {})
    }

    get csrfToken() {
        return document
            .querySelector("meta[name='csrf-token']")
            ?.content
    }
}
